/**
 * widget-factory — tworzenie nowej instancji widżetu + dokładanie jej do
 * geometrii siatki przy „Dodaj widżet" (tryb edycji).
 *
 * `createWidget` bierze domyślny config z rejestru i nadaje świeże `id`
 * (`crypto.randomUUID`). `appendWidgetToLayouts` kładzie kafelek na dole każdego
 * breakpointu (x:0, y = max dół), z szerokością przyciętą do liczby kolumn
 * danego breakpointu — żeby nie wystawał poza siatkę na wąskich ekranach.
 */

import type { WidgetVisualType } from '@/lib/widgets/types';

import type { Breakpoint, DashboardWidget, WidgetLayoutItem, WidgetType } from './types';
import { WIDGET_REGISTRY } from './widget-registry';

const COLS: Record<Breakpoint, number> = { lg: 12, md: 10, sm: 6, xs: 4 };
const BREAKPOINTS: Breakpoint[] = ['lg', 'md', 'sm', 'xs'];

export function createWidget(
  type: WidgetType,
  configPatch?: Record<string, unknown>,
): DashboardWidget {
  const entry = WIDGET_REGISTRY[type];
  // Rejestr gwarantuje zgodność `type` ↔ `config`; union nie zawęża się z
  // generycznego `WidgetType`, stąd jednorazowy cast w jednym miejscu.
  // `configPatch` = preset z palety (np. wykres słupkowy pracowników).
  return {
    id: crypto.randomUUID(),
    type,
    config: { ...entry.defaultConfig(), ...configPatch },
  } as DashboardWidget;
}

/**
 * Rozmiar startowy kafla „custom" wg typu wizualnego definicji (rejestr ma jeden `defaultSize`
 * per typ, a „custom" mieści KPI/wykres/tabelę o różnych sensownych rozmiarach).
 */
export function customWidgetSize(visualType: WidgetVisualType): {
  w: number;
  h: number;
  minW: number;
  minH: number;
} {
  if (visualType === 'kpi') return { w: 4, h: 2, minW: 2, minH: 2 };
  if (visualType === 'table') return { w: 8, h: 5, minW: 4, minH: 3 };
  return { w: 6, h: 4, minW: 3, minH: 3 }; // wykresy
}

/**
 * Tworzy instancję kafla „custom" z wybranej definicji. Snapshotuje `visualType` (mikro-dispatch
 * wykresu bez dodatkowego fetcha metadanych), nazwę (tytuł kafla) i ikonę definicji.
 */
export function createCustomWidget(def: {
  id: string;
  name: string;
  icon: string | null;
  visualType: WidgetVisualType;
}): DashboardWidget {
  return {
    id: crypto.randomUUID(),
    type: 'custom',
    config: {
      definitionId: def.id,
      visualType: def.visualType,
      title: def.name,
      ...(def.icon ? { icon: def.icon } : {}),
    },
  };
}

export function appendWidgetToLayouts(
  layouts: Record<Breakpoint, WidgetLayoutItem[]>,
  widgetId: string,
  defaultSize: { w: number; h: number; minW: number; minH: number },
): Record<Breakpoint, WidgetLayoutItem[]> {
  const next = {} as Record<Breakpoint, WidgetLayoutItem[]>;
  for (const bp of BREAKPOINTS) {
    const items = layouts[bp] ?? [];
    const cols = COLS[bp];
    const bottom = items.reduce((max, it) => Math.max(max, it.y + it.h), 0);
    const w = Math.min(defaultSize.w, cols);
    next[bp] = [
      ...items,
      {
        i: widgetId,
        x: 0,
        y: bottom,
        w,
        h: defaultSize.h,
        minW: Math.min(defaultSize.minW, w),
        minH: defaultSize.minH,
      },
    ];
  }
  return next;
}

export function removeWidgetFromLayouts(
  layouts: Record<Breakpoint, WidgetLayoutItem[]>,
  widgetId: string,
): Record<Breakpoint, WidgetLayoutItem[]> {
  const next = {} as Record<Breakpoint, WidgetLayoutItem[]>;
  for (const bp of BREAKPOINTS) {
    next[bp] = (layouts[bp] ?? []).filter((it) => it.i !== widgetId);
  }
  return next;
}
